import {forwardRef, useEffect, useState} from 'react';
import {Autocomplete, CircularProgress, TextField} from '@mui/material';
import useDebounce from 'ahooks/es/useDebounce';

import {ApiSportsMappingValue, MappingKind} from '../../api/types';
import {useGetMappings} from '../../apiHooks';

type Props = {
  value: ApiSportsMappingValue | null;
  onChange: (value: ApiSportsMappingValue | null) => void;
  kind: MappingKind;
  categorySlug?: string;
  entityId?: string;
  label?: string;
  disabled?: boolean;
  // Required by the matches search; ignored for the other kinds.
  season?: string;
};

// Label without the ID — used for the search result options.
const getOptionName = (option: ApiSportsMappingValue) =>
  option.country ? `${option.name} (${option.country})` : option.name;

// Label with the ID — used for the selected (filled) value.
const getOptionLabel = (option: ApiSportsMappingValue) =>
  `${getOptionName(option)} (ID: ${option.id})`;

export const ApiSportsMappingSelect = forwardRef<HTMLInputElement, Props>(
  (
    {value, onChange, kind, categorySlug, entityId, label, disabled, season},
    ref,
  ) => {
    const [inputValue, setInputValue] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearch = useDebounce(searchQuery, {wait: 400});

    const {data, isPending} = useGetMappings(
      categorySlug,
      kind,
      entityId,
      debouncedSearch || undefined,
      season,
    );

    const options = data?.values ?? [];

    // Enrich a prefilled value (coming from the metadata apisports ref, which
    // only has id + name) with the full record (country/logo) once the
    // mappings request resolves and contains a matching entry.
    useEffect(() => {
      if (!value || value.country) return;
      const match = options.find(o => o.id === value.id);
      if (match && (match.country || match.logo)) {
        onChange(match);
      }
    }, [options, value, onChange]);

    const isValueInOptions = value
      ? options.some(o => o.id === value.id)
      : true;
    const optionsWithSelected =
      !isValueInOptions && value ? [value, ...options] : options;

    return (
      <Autocomplete
        ref={ref}
        value={value}
        inputValue={inputValue}
        options={optionsWithSelected}
        loading={isPending}
        disabled={disabled}
        filterOptions={x => x}
        getOptionLabel={getOptionLabel}
        renderOption={(props, option) => {
          const {key, ...rest} = props;
          return (
            <li key={key} {...rest}>
              {getOptionName(option)}
            </li>
          );
        }}
        isOptionEqualToValue={(opt, val) => opt.id === val.id}
        onInputChange={(_, newInput, reason) => {
          setInputValue(newInput);
          if (reason === 'input') setSearchQuery(newInput);
          else setSearchQuery('');
        }}
        onChange={(_, newValue) => onChange(newValue)}
        renderInput={params => (
          <TextField
            {...params}
            label={label}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isPending && <CircularProgress size={16} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />
    );
  },
);
